import { BlockchainNetwork, ConstantsHelper, POSITION_LIMIT, SOL, WalletType } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"

import { ObligationType } from "@/atoms/obligations"
import { SelectedReserveType } from "@/atoms/shared"
import { NATIVE_MINT } from "@solana/spl-token"
import { RENEC } from "../constants"
import { replaceLocaleStringWithValues } from "../helpers"
import {
  FIAT_DECIMALS,
  RENEC_PADDING_FOR_RENT_AND_FEE,
  SOL_PADDING_FOR_RENT_AND_FEE,
  sufficientNativeTokenForTransaction,
  sufficientNativeTokenForTransactionWithValue,
} from "./commons"

export const supplyConfigs = {
  verifyAction: (
    network: BlockchainNetwork,
    value: BigNumber,
    obligation: ObligationType | null,
    reserve: SelectedReserveType,
    wallet: WalletType,
    trans: any,
  ) => {
    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    const isFiatToken = reserve.decimals === FIAT_DECIMALS
    const roundingStrategy = isFiatToken ? BigNumber.ROUND_DOWN : undefined

    // Check basic amount validity first
    const valueRounded = value.decimalPlaces(reserve.decimals, roundingStrategy)
    if (valueRounded.isZero() || !valueRounded) {
      return trans.dashboard.modal.errors.please_enter_amount
    }

    // Check wallet balance
    const tokenBalance =
      wallet
        .find((a) => a.mintAddress === reserve.mintAddress)
        ?.amount.decimalPlaces(reserve.decimals, roundingStrategy) ?? BigNumber(0)

    if (valueRounded.isGreaterThan(tokenBalance) || tokenBalance.isZero()) {
      return trans.dashboard.modal.errors.insufficient_balance(reserve.symbol)
    }

    // Check native token padding requirements
    const isRenecOrSolanaLiquidity =
      (reserve.symbol === RENEC && isRenecChain) || (reserve.symbol === SOL && isSolanaChain)
    const amountPadding = isRenecChain ? RENEC_PADDING_FOR_RENT_AND_FEE : SOL_PADDING_FOR_RENT_AND_FEE

    const isInSufficient = isRenecOrSolanaLiquidity
      ? !sufficientNativeTokenForTransactionWithValue(network, wallet, valueRounded)
      : !sufficientNativeTokenForTransaction(network, wallet)

    if (isInSufficient) {
      const nativeMintBalance =
        wallet.find((ass) => ass.mintAddress === NATIVE_MINT.toBase58())?.amount ?? new BigNumber(0)
      const minNativeFee = isRenecOrSolanaLiquidity
        ? new BigNumber(amountPadding).minus(nativeMintBalance.minus(value)).toString()
        : new BigNumber(amountPadding).minus(nativeMintBalance).toString()
      return replaceLocaleStringWithValues(
        trans.dashboard.modal.errors.min_native_fee,
        minNativeFee,
        isRenecChain ? RENEC : SOL,
      )
    }

    // If there's no obligation, we've already done all necessary checks
    if (!obligation) return null

    // Check position limit only if there is an obligation
    const positionLimitReached =
      obligation.positions > POSITION_LIMIT &&
      !obligation.deposits.map((d) => d.reserveAddress).includes(reserve.address)

    if (positionLimitReached) {
      return trans.dashboard.modal.errors.max_positions_reached
    }

    // Check reserve supply cap
    if (
      reserve.totalSupply
        .plus(valueRounded)
        .isGreaterThan(reserve.reserveSupplyCap.decimalPlaces(reserve.decimals, roundingStrategy))
    ) {
      return trans.dashboard.modal.errors.over_reserve_deposit_limit
    }

    return null
  },
  getNewCalculations: (obligation: ObligationType | null, reserve: SelectedReserveType, value: string) => {
    if (!obligation) {
      return {
        borrowLimit: null,
        newBorrowLimit: null,
        utilization: null,
        newBorrowUtilization: null,
        calculatedBorrowFee: null,
      }
    }

    const valueObj = new BigNumber(value)

    const newBorrowLimit = !valueObj.isNaN()
      ? obligation.minPriceBorrowLimit.plus(valueObj.times(reserve.minPrice).times(reserve.loanToValueRatio))
      : null

    const utilization = !obligation.totalSupplyValue.isZero()
      ? obligation.totalBorrowValue.dividedBy(obligation.totalSupplyValue)
      : null

    const newTotalSupply = obligation.totalSupplyValue.plus(valueObj.times(reserve.minPrice))

    const newBorrowUtilization =
      newTotalSupply && !newTotalSupply.isZero() ? obligation.totalBorrowValue.dividedBy(newTotalSupply) : null

    return {
      borrowLimit: obligation.minPriceBorrowLimit,
      newBorrowLimit,
      utilization: utilization,
      newBorrowUtilization,
      calculatedBorrowFee: null,
    }
  },
  calculateMax: (network: BlockchainNetwork, reserve: SelectedReserveType, wallet: WalletType) => {
    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    const amountPadding = isRenecChain ? RENEC_PADDING_FOR_RENT_AND_FEE : SOL_PADDING_FOR_RENT_AND_FEE

    const supplyCapRemaining = reserve.reserveSupplyCap.minus(reserve.totalSupply).minus(0.2)
    const isFiatToken = reserve.decimals === FIAT_DECIMALS

    const asset = wallet.find((ass) => ass.mintAddress === reserve.mintAddress)

    if (!asset) return new BigNumber(0)

    const isRenecOrSolanaLiquidity =
      (reserve.symbol === RENEC && isRenecChain) || (reserve.symbol === SOL && isSolanaChain)

    const maxSuppliableFromWallet = isRenecOrSolanaLiquidity ? asset.amount.minus(amountPadding) : asset.amount

    const roundingStrategy = isFiatToken ? BigNumber.ROUND_DOWN : undefined

    return BigNumber.max(BigNumber.min(maxSuppliableFromWallet, supplyCapRemaining), new BigNumber(0)).decimalPlaces(
      reserve.decimals,
      roundingStrategy,
    )
  },
}
